import React, { useState } from 'react';
import MarkdownText from './MarkdownText';
import { useLinkPreview, PreviewCard } from './LinkPreview';
import { stripShareLink } from './chatUnfurl';
import { imagePostIn, imageFailure } from './chatFiles';

/**
 * ChatMessageBody — a posted message's text and its link preview, together.
 *
 * share-link-unfurl. These are ONE component because they are one decision: a
 * message drops the raw URL from its bubble only when a card has actually
 * resolved to stand in for it. Rendered as siblings, the bubble would have to
 * guess, and would guess wrong every time a record is deleted or the network
 * blinks — the URL would vanish and nothing would replace it.
 *
 * Used for messages a PERSON posted (own + room), and for an assistant message that
 * carries a card link: a turn's cards, or one the assistant reopened.
 *
 * The message on the wire is untouched — this is render-time only, so room
 * history, older clients, and copy-paste all still carry the link.
 */
// A gone or unloadable image; `imageFailure` tells the two apart.
const goneStyle = { padding: '8px 12px', borderRadius: 12, border: '1px dashed #cbd5e1',
                    color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' };

export default function ChatMessageBody({ text, bubbleStyle, onNavigate, onOpenShared, openHint,
                                         cardFallback = false, compact = false,
                                         onOpenImage = null }) {
  const { preview, text: linkText, url, status } = useLinkPreview(text);
  const [imageFailed, setImageFailed] = useState(null);
  // An image post shows as the picture alone; the Output panel it opens in has the download.
  const image = onOpenImage ? imagePostIn(text) : null;
  if (image && imageFailed === 'gone') {
    return <div data-testid="chat-image-gone" style={goneStyle}>Image no longer available</div>;
  }
  if (image && imageFailed) {
    return (
      <button type="button" data-testid="chat-image-failed" onClick={() => setImageFailed(null)}
              style={{ ...goneStyle, cursor: 'pointer', background: 'none' }}>
        Image couldn't be loaded. Tap to retry
      </button>
    );
  }
  if (image) {
    return (
      <button type="button" data-testid="chat-image" title="Open in the output panel"
              onClick={() => onOpenImage(image)}
              style={{ maxWidth: '80%', padding: 0, border: 'none', background: 'none',
                       cursor: 'zoom-in', lineHeight: 0 }}>
        {/* The small server-made preview, fetched only when scrolled near; the panel shows
            the full file. */}
        <img src={`${image.url}?size=preview`} alt={image.name} loading="lazy" decoding="async"
             onError={(e) => {
               if (e.currentTarget.src.endsWith(image.url)) imageFailure(image.url).then(setImageFailed);
               else e.currentTarget.src = image.url;
             }}
             style={{ display: 'block', maxWidth: '100%', maxHeight: 180, borderRadius: 8,
                      objectFit: 'cover' }} />
      </button>
    );
  }
  // A card link never shows as a raw URL: hidden while its preview loads, a button if it fails.
  const asCard = cardFallback && url && status !== 'ready';
  const shown = asCard ? stripShareLink(text, url) : linkText;
  return (
    <>
      {/* A message that was ONLY a link renders as the card alone — an empty
          bubble above it would be a visible hole where the URL used to be. */}
      {/* chat-long-token-wrap: `anywhere` lets a pasted URL break inside a pre-wrap bubble
          (which wraps only at spaces) instead of pushing the page sideways; it, not
          `break-word`, lowers min-content. <pre> is untouched; callers can override. */}
      {shown ? <div style={{ overflowWrap: 'anywhere', ...bubbleStyle }}><MarkdownText text={shown} chat /></div> : null}
      {preview && (
        <PreviewCard preview={preview} onNavigate={onNavigate} compact={compact}
                     onOpenShared={onOpenShared} openHint={openHint} />
      )}
      {asCard && status === 'failed' && onOpenShared && (
        <button type="button" data-testid="card-link-fallback"
                onClick={() => onOpenShared({ id: url.split('/').pop(), url, title: null })}
                style={{ marginTop: 4, fontSize: '0.8rem', border: '1px solid #cbd5e1',
                         borderRadius: 8, background: '#fff', padding: '4px 10px',
                         cursor: 'pointer', color: '#334155' }}>
          Open card
        </button>
      )}
    </>
  );
}
